import { endOfDay, startOfDay } from "date-fns";
import React from "react";

import { DateRangePicker, FormCheckboxGroup, FormField, PanelFilters } from "@performyard/py-components";
import { capitalizeWord } from "@performyard/utils-isomorphic";

import { SingleSelectEmployee } from "../../../components/Inputs/SelectEmployee";
import type { EmployeeValue } from "../../../components/Inputs/SelectEmployee";

import { config } from "../config";
import {
	type CompanyMeetingsFilterForm,
	CompanyMeetingsFilterFormSchema,
	type CompanyMeetingsFilters,
} from "../schema";

type CompanyMeetingsFilterPanelProps = {
	isOpen: boolean;
	onClose: () => void;
	filters: CompanyMeetingsFilters;
	onApplyFilters: (filters: CompanyMeetingsFilters) => void;
};

const statusOptions = config.statusOptions.map((status) => ({
	value: status,
	label: capitalizeWord(status),
}));

export const CompanyMeetingsFilterPanel = ({
	isOpen,
	onClose,
	filters,
	onApplyFilters,
}: CompanyMeetingsFilterPanelProps) => {
	const defaultValues: CompanyMeetingsFilterForm = {
		participant: filters.participant ?? null,
		dateRange:
			filters.dateRangeStart || filters.dateRangeEnd
				? { from: filters.dateRangeStart, to: filters.dateRangeEnd }
				: undefined,
		status: filters.status ?? [],
	};

	const handleSubmit = ({
		value,
	}: {
		value: CompanyMeetingsFilterForm;
		resetForm: () => void;
		setError: (field: keyof CompanyMeetingsFilterForm, error: string) => void;
	}) => {
		const appliedFilters: Partial<CompanyMeetingsFilters> = {};

		if (value.participant) {
			appliedFilters.participant = value.participant;
		}

		if (value.dateRange?.from) {
			appliedFilters.dateRangeStart = startOfDay(value.dateRange.from);
		}

		if (value.dateRange?.to) {
			appliedFilters.dateRangeEnd = endOfDay(value.dateRange.to);
		}

		if (value.status && value.status.length > 0) {
			appliedFilters.status = value.status as CompanyMeetingsFilters["status"];
		}

		onApplyFilters(appliedFilters);
		onClose();
	};

	return (
		<PanelFilters
			isOpen={isOpen}
			onClose={onClose}
			schema={CompanyMeetingsFilterFormSchema}
			defaultValues={defaultValues}
			onSubmit={handleSubmit}
		>
			<div className="space-y-4">
				<FormField<EmployeeValue | null>
					id="participant"
					label="Participant"
					input={(field) => (
						<SingleSelectEmployee
							value={field.value ?? undefined}
							onChange={(id, name) => {
								if (id && name) {
									field.onChange({ id, name });
								} else {
									field.onChange(null);
								}
							}}
							placeholder="Search for an employee..."
						/>
					)}
				/>

				<FormField<{ from?: Date; to?: Date } | undefined>
					id="dateRange"
					label="Meeting Date"
					input={(field) => (
						<DateRangePicker
							id="meeting-date-filter"
							value={field.value}
							onChange={field.onChange}
							popoverContentClassName="z-[1100]"
							menuAlign="start"
							hasClearButton
						/>
					)}
				/>

				<FormCheckboxGroup id="status" label="Status" options={statusOptions} />
			</div>
		</PanelFilters>
	);
};

export default CompanyMeetingsFilterPanel;
